import type { Route } from "@route-types/Article/+types/Article";
import styles from "@src/app/components/Article/Article.css?url";
import ArticleAttachments from "@src/app/components/Article/ArticleAttachments";
import ArticleAuthorBar from "@src/app/components/Article/ArticleAuthorBar";
import ArticleBody from "@src/app/components/Article/ArticleBody";
import ArticleJoinUs from "@src/app/components/Article/ArticleJoinUs";
import ArticleMainMedia from "@src/app/components/Article/ArticleMainMedia";
import ArticleRecommend from "@src/app/components/Article/ArticleRecommend";
import ArticleStandfirst from "@src/app/components/Article/ArticleStandfirst";
import ArticleTags from "@src/app/components/Article/ArticleTags";
import ArticleTopArticles from "@src/app/components/Article/ArticleTopArticles";
import ClientOnly from "@src/app/components/ClientOnly/ClientOnly";
import { createMetaTags } from "@src/app/components/MetaTags/MetaTags";
import { SliceTag } from "@src/app/components/SectionTag";
import SocialShare, {
  SocialShareLinks,
} from "@src/app/components/SocialShare/SocialShare";
import {
  parsePayload,
  useRouteResponse,
} from "@src/app/hooks/useRouteResponse";
import { Section } from "@src/app/types/Data";
import type { ArticlePageResponse } from "@src/app/types/response";
import { useEffect, useRef, useState } from "react";
import type { LinksFunction } from "react-router";

import { fetchArticlePageData } from "./Article.server";
import VideoArticle from "./VideoArticle";

export const loader = fetchArticlePageData;

export const meta = (args: Route.MetaArgs) => {
  const payload = parsePayload<ArticlePageResponse>(args.loaderData);
  const article = payload.article;
  const { metaTags, ogTags } = article || {};

  // Get main media image
  const mainMediaImageUrl =
    article?.section === Section.VIDEOS
      ? article.mainMedia.find((i) => i.type === "youtube_video")?.poster || ""
      : article?.mainMedia.find((i) => i.type === "image")?.url || "";

  const title = metaTags?.title || `${article?.title || ""} - 红蚂蚁`;
  const description =
    metaTags?.description || `${article?.teaser || ""} 点击阅读全文。`;
  return createMetaTags({
    title,
    description,
    canonical:
      metaTags?.canonical || `https://www.redants.sg${article?.urlPath || ""}`,
    keywords: (article?.tags || []).map((i) => i.name).join(","),
    robots: "index, follow, max-image-preview:large",
    ogInfo: {
      ogType: "article",
      ogTitle: ogTags?.title || `${article?.title || ""} - 红蚂蚁`,
      ogDescription:
        ogTags?.description || `${article?.standfirst || ""} 点击阅读全文。`,
      ogImage: ogTags?.image || mainMediaImageUrl || "",
    },
  });
};

export const links: LinksFunction = () => {
  return [...SocialShareLinks(), { rel: "stylesheet", href: styles }];
};

export default function ArticlePage() {
  const res = useRouteResponse("articlePage");

  const article = res.article;
  // Get social share position
  const articleHeaderRef = useRef<HTMLDivElement>(null);
  const [socialSharePosition, setSocialSharePosition] = useState<string>("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      const header = document.querySelector("#header");
      const socialSharePos = `${(header?.clientHeight ?? 0) + (articleHeaderRef.current?.offsetHeight ?? 0)}px`;
      setSocialSharePosition(socialSharePos);
    }
  }, []);

  return res.article?.section == Section.VIDEOS ? (
    <VideoArticle />
  ) : (
    <div className="bg-secondary">
      <SocialShare
        className="fixed left-6 z-50 hidden lg:flex"
        iconOnly
        style={{ top: socialSharePosition }}
        articleTitle={article?.title || ""}
        articleUrl={article?.urlPath || ""}
      />
      <div className="article mx-auto box-content px-6 lg:max-w-[944px] xl:max-w-[1200px]">
        {/* main part */}
        <div className="mx-auto xl:w-10/12">
          <div ref={articleHeaderRef} className="py-4">
            {article?.section && (
              <SliceTag section={article.section as Exclude<Section, "home">} />
            )}

            <h1 className="py-4 text-justify text-header-1 text-primary">
              {article?.title}
            </h1>

            <ClientOnly>
              <ArticleAuthorBar />
            </ClientOnly>
          </div>

          <ArticleMainMedia />
        </div>

        {/* article body */}
        <div className="mx-auto lg:w-10/12 xl:w-8/12">
          <ArticleStandfirst />

          <ArticleBody />

          <ArticleAttachments />

          <ArticleRecommend />

          <ArticleTags />

          <ArticleJoinUs />
        </div>

        <ArticleTopArticles />
      </div>
    </div>
  );
}
